import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Result, Typography, Button, Alert } from 'antd';
import { PlayCircleOutlined, ShareAltOutlined } from '@ant-design/icons';
import { micrositioService, fixUrl } from '../../services/api';
import { SiteFooter, SiteHeader, colapsarAgrupados } from './RematePage';
// Los helpers del hero salen de utils/hero y no del re-export de RematePage:
// ese re-export existe por compatibilidad con las páginas que ya los
// importaban desde ahí, no es una API. Un archivo nuevo va a la fuente.
import { heroBackground, heroTextLayout, heroTextPadding, heroMargenSuperior } from '../../utils/hero';
import PujaSpinner from '../../components/PujaSpinner';
import AuthContext from '../../context/AuthContext';
import { TIPOGRAFIAS, altoHeaderDe } from '../../utils/micrositioTema';
import { urlEmbedStreaming, urlExternaStreaming } from '../../utils/streaming';
import { getStreamingShareUrl } from '../../services/shareUrls';
import { useAvisarAltoAlPadre } from '../../hooks/useAvisarAltoAlPadre';

const { Title, Text } = Typography;

/**
 * Transmisión en vivo del remate físico, incrustada en el micrositio.
 *
 * Misma estructura que las demás páginas internas (header + hero + contenido +
 * footer) a propósito: el visitante no siente que se fue a otro lado, que es
 * todo el punto de no mandarlo a YouTube.
 */
const EnVivoPage = () => {
  // Le avisa al sitio incrustante cuánto medimos, para que agrande el
  // iframe en vez de dejarlo clavado en los 800px del snippet.
  useAvisarAltoAlPadre();

  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const cargar = async () => {
      try {
        const res = await micrositioService.getBySlug(slug);
        setData(res.data);
      } catch (err) {
        // Sin `message.error` a propósito: esto no es una acción puntual que
        // falla, es la carga entera de la pantalla. El error se comunica con
        // el <Result> a página completa de más abajo, que dice más que un
        // toast que se va en tres segundos — y un toast ADEMÁS sería ruido
        // duplicado. Mismo criterio que CondicionesPage/ContactoPage.
        setError(err.response?.status === 404 ? 'not_found' : 'server_error');
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [slug]);

  if (loading) {
    return <PujaSpinner size="lg" label="Cargando…" fondo="#fff" />;
  }

  if (error === 'not_found') {
    return <Result status="404" title="Remate no encontrado" />;
  }

  if (error) {
    return <Result status="500" title="Error al cargar" />;
  }

  const { evento, categorias } = data;
  const config = evento.configuracion_micrositio || {};
  const tipografia = TIPOGRAFIAS[config.tipografia_combo] || TIPOGRAFIAS.moderna;
  const tipoCabecera = config.cabecera || 'cabecera-1';
  // `altoHeaderDe` y no la tabla a mano: el panel calcula con el el margen
  // automatico que le MUESTRA al productor, y dos cuentas del mismo numero es
  // justo la divergencia que `micrositioTema.js` existe para matar.
  const headerHeight = altoHeaderDe(config);
  const isTwoRow = tipoCabecera === 'cabecera-2' || tipoCabecera === 'cabecera-3';
  const heroOff = !!config.hero_off_internas;
  const colorHeaderInitial = config.color_header_initial || 'transparent';
  const heroTransparente = !heroOff && !isTwoRow && colorHeaderInitial === 'transparent';

  // Compartir el vivo por WhatsApp. Mismo canal y mismo armado que el share de
  // animales de RematePage: es el canal por el que se mueve todo en un remate.
  const handleCompartirVivo = () => {
    const url = getStreamingShareUrl({ config, eventoSlug: slug });
    const nomEvento = (evento.nombre_evento || '').trim();
    const texto = `Mirá el remate ${nomEvento} EN VIVO:

${url}`;
    // Dominio FIJO, asi que no necesita lista blanca — pero el `noopener` si:
    // no depende de a donde se va, sino de que la pestaña nueva no pueda
    // tocar `window.opener` y redirigir ESTA pagina.
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener,noreferrer');
  };
  const heroMarginTop = heroMargenSuperior(config, headerHeight, heroTransparente);
  const heroPaddingTop = heroTransparente ? headerHeight : 0;
  const configHeader = heroOff
    ? { ...config, color_header_initial: config.color_header || config.color_primary || '#1a5f2a' }
    : config;

  const cssVars = {
    '--color-primary': config.color_primary || '#1a5f2a',
    '--color-secondary': config.color_secondary || '#8B4513',
  };

  const heroVars = {
    '--hero-margin-top':  `${heroMarginTop}px`,
    '--hero-padding-top': `${heroPaddingTop}px`,
  };

  // Terminado el remate no se muestra ni el reproductor ni el link, por el
  // mismo motivo por el que RematePage no dibuja el banner: esta página se
  // comparte por WhatsApp y alguien la abre tres días después. Un encabezado
  // que dice "Transmisión en vivo" sobre un reproductor muerto es la misma
  // mentira, en otra pantalla.
  const finalizado = !!evento.es_finalizado;
  const embed = finalizado ? null : urlEmbedStreaming(config.url_streaming);
  const urlExterna = finalizado ? null : urlExternaStreaming(config.url_streaming);
  // El encabezado también: "Transmisión en vivo" arriba de un cartel que dice
  // que el remate terminó es la contradicción que este gate vino a evitar.
  const titulo = finalizado
    ? 'Transmisión del remate'
    : ((config.titulo_streaming || '').trim() || 'Transmisión en vivo');

  const animalesMenu = colapsarAgrupados(data.animales || []);
  const razas = [...new Set(
    animalesMenu.filter(a => a.nom_raza).map(a => a.nom_raza)
  )].sort();
  const cabanas = [...new Set(
    animalesMenu.filter(a => a.nom_cabana).map(a => a.nom_cabana)
  )].sort();

  return (
    <div
      className="micrositio-page"
      style={{ fontFamily: tipografia.body, minHeight: '100vh', ...cssVars, ...heroVars }}
    >
      <SiteHeader
        config={configHeader}
        tipografia={tipografia}
        logoUrl={evento.img_evento}
        evento={evento}
        categorias={categorias}
        razas={razas}
        cabanas={cabanas}
        scrolled={scrolled}
        slug={slug}
        isAuthenticated={isAuthenticated}
      />

      {/* ── HERO (oculto si hero_off_internas) ── */}
      {heroOff ? (
        <div className="hero-off-spacer" style={{ '--hero-off-header': `${headerHeight}px` }} />
      ) : (
        <>
          <div
            className="hero-zone"
            style={{
              background: heroBackground(config, evento.imgsec_evento ? fixUrl(evento.imgsec_evento) : null),
              minHeight: config.hero_altura || (heroTransparente ? 260 : 180),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              ...heroTextLayout(config, 'column'),
            }}
          >
            <div style={{ textAlign: 'center', paddingLeft: 24, paddingRight: 24, ...heroTextPadding(config) }}>
              <Title
                level={1}
                style={{
                  color: config.color_hero_text || '#ffffff',
                  fontFamily: tipografia.title,
                  fontSize: config.hero_text_size || 32,
                  margin: 0,
                  textTransform: config.text_transform_hero || 'none',
                }}
              >
                {evento.nombre_evento}
              </Title>
            </div>
          </div>

          {/* ── DIVIDER ── */}
          <div style={{ height: 5, background: config.color_primary || '#1a5f2a' }} />
        </>
      )}

      {/* ── CONTENIDO ──
          1100px, como ContactoPage. Las de condiciones usan 900, que es el
          ancho cómodo para leer texto; un reproductor de video ahí queda
          chico para lo único que la gente viene a hacer a esta página. */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '64px 24px 80px' }}>
        {/* El compartir va ARRIBA, con el titulo: el que quiere pasar el vivo
            lo quiere pasar mientras esta pasando, no despues de scrollear. */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 24,
          }}
        >
          <Title
            level={2}
            style={{
              fontFamily: tipografia.title,
              color: config.color_title || '#1a1a1a',
              margin: 0,
            }}
          >
            {titulo}
          </Title>

          <Button
            icon={<ShareAltOutlined />}
            onClick={handleCompartirVivo}
            style={{ borderRadius: 32, fontFamily: tipografia.body }}
          >
            Compartir
          </Button>
        </div>

        {embed ? (
          /* padding-top 56.25% = 16:9. Con altura fija, el video queda con
             bandas negras en mobile y desbordado en pantallas anchas. */
          <div
            style={{
              position: 'relative',
              paddingTop: '56.25%',
              background: '#000',
              borderRadius: 10,
              overflow: 'hidden',
              boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
            }}
          >
            <iframe
              src={embed.url}
              title={titulo}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            />
          </div>
        ) : urlExterna ? (
          /* Hay link cargado pero no se puede incrustar (un canal, un
             proveedor que lo prohíbe). Antes que un cuadro negro, el link
             que sí funciona. */
          <Alert
            type="info"
            showIcon
            message="La transmisión se ve en el sitio del proveedor"
            description={
              <div style={{ marginTop: 12 }}>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  href={urlExterna}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: config.color_btn_primary || config.color_primary || '#1a5f2a',
                    border: 'none',
                    borderRadius: 32,
                  }}
                >
                  Abrir la transmisión
                </Button>
              </div>
            }
          />
        ) : (
          /* Sin link cargado: la página existe pero no hay nada que mostrar.
             El visitante llegó por un link viejo o por la barra de direcciones. */
          <Text style={{ color: '#999', fontFamily: tipografia.body }}>
            {finalizado
              ? 'El remate ya finalizó. Gracias por participar.'
              : 'En este momento no hay una transmisión en vivo.'}
          </Text>
        )}
      </div>

      <SiteFooter
        config={config}
        tipografia={tipografia}
        evento={evento}
        categorias={categorias}
        logoUrl={evento.img_evento}
        onCategoriaSelect={(id) => navigate(`/remate/${slug}`, { state: { categoriaFiltro: id } })}
        slug={slug}
      />
    </div>
  );
};

export default EnVivoPage;
